import { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `${v < 0 ? '-' : ''}$${Math.abs(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PERIODS = [['today', 'Today'], ['yesterday', 'Yesterday'], ['last7', 'Last 7 days']];

const Row = ({ g, testid }) => (
    <tr className={`border-t border-white/5 ${g.alert ? 'bg-red-500/10' : ''}`} data-testid={testid}>
        <td className="py-2 pr-3 font-semibold text-white">
            {g.alert && <AlertTriangle className="mr-1 inline h-3.5 w-3.5 text-red-400" data-testid={`${testid}-alert`} />}
            {g.label}
        </td>
        <td className="py-2 pr-3 text-right tabular-nums">{money(g.bets)}</td>
        <td className="py-2 pr-3 text-right tabular-nums">{money(g.paid)}</td>
        <td className="py-2 pr-3 text-right tabular-nums text-amber-300">{g.jackpots ? money(g.jackpots) : '—'}</td>
        <td className={`py-2 pr-3 text-right font-bold tabular-nums ${g.alert ? 'text-red-400' : (g.payback_pct ?? 0) > 88 ? 'text-amber-300' : 'text-[#39FF14]'}`}
            data-testid={`${testid}-pct`}>{g.payback_pct == null ? '—' : `${g.payback_pct}%`}</td>
        <td className={`py-2 text-right font-bold tabular-nums ${g.profit < 0 ? 'text-red-400' : 'text-[#39FF14]'}`}>{money(g.profit)}</td>
    </tr>
);

export const usePaybackReport = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const load = () => {
        setError(false);
        return axios.get(`${API}/admin/payback-report`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => setError(true));
    };
    useEffect(() => { load(); }, []);
    return [data, load, error];
};

export default function PaybackReportPanel() {
    const [data, load, error] = usePaybackReport();
    const [period, setPeriod] = useState('today');
    const rep = data?.[period];
    return (
        <div className="space-y-4" data-testid="payback-report">
            <div className="flex flex-wrap items-center gap-2">
                <h2 className="mr-auto text-lg font-black text-white">Daily Payback</h2>
                {PERIODS.map(([k, label]) => (
                    <Button key={k} size="sm" variant={period === k ? 'default' : 'outline'} onClick={() => setPeriod(k)}
                        className={period === k ? 'bg-[#39FF14] text-black hover:bg-[#39FF14]/90' : 'border-white/15 text-white'} data-testid={`payback-period-${k}`}>{label}</Button>
                ))}
                <Button size="sm" variant="ghost" onClick={load} className="text-white/70" data-testid="payback-refresh"><RefreshCw className="h-4 w-4" /></Button>
            </div>
            <p className="text-xs text-[#A0AAB2]">
                Business day 7:00 AM → 7:00 AM Central. Paid includes shared jackpots won on that game. Red flag = over {data?.alert_pct ?? 90}% payback with at least ${data?.min_bets ?? 50} bet.
            </p>
            {error && !rep && <p className="text-red-400" data-testid="payback-error">Couldn't load the report. Tap refresh to try again.</p>}
            {!rep ? (!error && <p className="text-white/60">Loading…</p>) : (
                <div className="overflow-x-auto rounded-xl border border-white/10 bg-black/30 p-3">
                    <table className="w-full text-sm text-[#cfd6dc]">
                        <thead>
                            <tr className="text-left text-[11px] uppercase tracking-wider text-[#A0AAB2]">
                                <th className="pb-2">Game</th><th className="pb-2 text-right">Bets</th><th className="pb-2 text-right">Paid</th>
                                <th className="pb-2 text-right">Jackpots</th><th className="pb-2 text-right">Payback</th><th className="pb-2 text-right">Profit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rep.games.map((g) => <Row key={g.game} g={g} testid={`payback-row-${g.game}`} />)}
                            {!rep.games.length && <tr><td colSpan={6} className="py-4 text-center text-white/50">No play yet in this period.</td></tr>}
                        </tbody>
                        <tfoot>
                            <tr className="border-t-2 border-white/20 font-black text-white" data-testid="payback-total">
                                <td className="pt-2">Total</td><td className="pt-2 text-right">{money(rep.total.bets)}</td>
                                <td className="pt-2 text-right">{money(rep.total.paid - rep.games.reduce((a, g) => a + g.jackpots, 0))}</td>
                                <td className="pt-2 text-right text-amber-300">{money(rep.games.reduce((a, g) => a + g.jackpots, 0))}</td>
                                <td className="pt-2 text-right">{rep.total.payback_pct == null ? '—' : `${rep.total.payback_pct}%`}</td>
                                <td className={`pt-2 text-right ${rep.total.profit < 0 ? 'text-red-400' : 'text-[#39FF14]'}`}>{money(rep.total.profit)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
}

export const PaybackAlertStrip = ({ onOpen }) => {
    const [data] = usePaybackReport();
    const flagged = (data?.today?.games || []).filter((g) => g.alert);
    if (!flagged.length) return null;
    return (
        <button type="button" onClick={onOpen} data-testid="payback-alert-strip"
            className="mb-3 flex w-full items-center gap-2 rounded-xl border border-red-500/50 bg-red-500/10 px-4 py-2 text-left text-sm font-bold text-red-300 hover:bg-red-500/15">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Over {data.alert_pct}% payback today: {flagged.map((g) => `${g.label} ${g.payback_pct}%`).join(' · ')}
        </button>
    );
};
